import { Link } from "react-router-dom";
import { VerifiedProBadge } from "@/components/VerifiedProBadge";
import { FavoriteButton } from "@/components/FavoriteButton";
import { SoldBadge } from "@/components/SoldBadge";
import { ReservedBadge } from "@/components/ReservedBadge";
import { formatDistanceToNow } from "date-fns";
import { fr, enUS, nl } from "date-fns/locale";
import { useLang } from "@/context/LangContext";
import {
  MapPin,
  Zap,
  Sparkles,
  Calendar,
  Clock,
  Gauge,
  Flame,
  ShieldCheck,
  Store,
} from "lucide-react";
import { StarRating } from "@/components/StarRating";
import { ListingCardActions } from "@/components/ListingCardActions";
import { categoryLabel } from "@/lib/categoryLabel";

const DATE_LOCALES = { fr, en: enUS, nl };
const NEW_LISTING_MS = 7 * 24 * 60 * 60 * 1000;
const HOT_VIEWS = 50;

function isNewListing(createdAt) {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() - t < NEW_LISTING_MS;
}

export function isRentalListing(listing) {
  const blob = [
    listing?.title,
    listing?.subcategory,
    listing?.machine_type,
    ...(listing?.highlights || []),
    listing?.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return /\blocation\b|\blouer\b|\brental\b|\bverhuur\b|\b\/\s*jour\b|\b€\/j/.test(blob);
}

export function ListingCard({
  listing,
  testId,
  selected = false,
  onSelect,
  compareSelected = false,
  onToggleCompare,
  showCompare = false,
  navigateOnClick = true,
  variant = "default",
  overrideTitle,
}) {
  const { t, lang } = useLang();
  const img = listing.images?.[0];
  const seller = listing.seller || {};
  const isPro = seller.role === "professionnel";
  const hasShop = seller.has_shop || isPro;
  const verified = !!seller.verified_pro;
  const compact = variant === "shop";
  const rental = isRentalListing(listing);
  const now = new Date().toISOString();
  const isPremium = listing.premium_until && listing.premium_until > now;
  const isHighlighted = !isPremium && listing.highlight_until && listing.highlight_until > now;
  const isNew = isNewListing(listing.created_at);
  const isHot = (listing.views_count || 0) >= HOT_VIEWS || (listing.favorites_count || 0) >= 5;
  const isSold = listing.status === "sold";
  const isReserved = listing.status === "reserved";
  const sellerRating = seller.rating;
  const hasSellerRating = (sellerRating?.count || 0) > 0;

  const stopBubble = (e) => {
    e.stopPropagation();
  };

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const photoBlock = (
    <div className="aspect-[4/3] w-full bg-zinc-100 overflow-hidden relative">
      {img ? (
        <img
          src={img}
          alt={listing.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-zinc-400 text-xs">{t("card.no_photo")}</div>
      )}

      <div className="absolute top-2 left-2 flex flex-col gap-1 items-start max-w-[70%]">
        {isSold && <SoldBadge size="sm" />}
        {!isSold && isReserved && <ReservedBadge size="sm" />}
        {!isSold && !isReserved && !compact && isNew && (
          <span className="inline-flex items-center gap-1 rounded-sm bg-sky-600 text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm">
            {t("card.badge_new")}
          </span>
        )}
        {!isSold && isPremium && (
          <span className="inline-flex items-center gap-1 rounded-sm bg-matpro-green text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm">
            <Sparkles className="h-3 w-3" /> {t("card.premium")}
          </span>
        )}
        {!isPremium && isHighlighted && (
          <span className="inline-flex items-center gap-1 rounded-sm bg-matpro-anthracite text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm">
            <Zap className="h-3 w-3" /> {t("card.highlighted")}
          </span>
        )}
        {!compact && isHot && (
          <span className="inline-flex items-center gap-1 rounded-sm bg-orange-500 text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm">
            <Flame className="h-3 w-3" /> {t("card.badge_hot")}
          </span>
        )}
        {compact && rental && !isSold && (
          <span className="inline-flex items-center gap-1 rounded-sm bg-white/95 text-matpro-green px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm">
            {t("card.rental")}
          </span>
        )}
        {!compact && isPro && (
          <span className="inline-flex items-center gap-1 rounded-sm bg-white/95 text-matpro-anthracite px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm">
            {t("card.pro")}
          </span>
        )}
        {!compact && verified && <VerifiedProBadge size="xs" className="shadow-sm" />}
      </div>

      {listing.delivery_available && (
        <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-sm bg-matpro-anthracite/90 text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
          <Zap className="h-3 w-3" /> {t("card.delivery")}
        </span>
      )}

      <div className="absolute top-2 right-2 bg-white/95 backdrop-blur rounded-md p-1.5 shadow-sm" onClick={stop}>
        <FavoriteButton listingId={listing.id} />
      </div>
    </div>
  );

  const bodyBlock = (
    <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-baseline gap-1.5 font-heading font-black text-2xl text-matpro-green leading-none">
          <span>{rental && listing.price_day != null ? formatPrice(listing.price_day) : formatPrice(listing.price)}</span>
          {rental ? (
            <span className="text-sm font-bold text-matpro-anthracite-muted">{t("card.per_day")}</span>
          ) : null}
        </div>
        {rental && listing.price_week != null && (
          <div className="mt-0.5 text-xs text-matpro-green/70 font-semibold">
            {formatPrice(listing.price_week)} {t("listing_detail.per_week")}
          </div>
        )}

        <h3 className="mt-2 font-heading font-bold text-base text-matpro-anthracite line-clamp-2 leading-tight min-h-[40px]">
          {overrideTitle || listing.title}
        </h3>

        {!compact && (listing.category_slug || listing.category_label) ? (
          <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.12em] text-matpro-anthracite-muted">
            {categoryLabel(listing.category_slug, t, listing.category_label)}
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-matpro-anthracite-muted">
          {listing.brand && (
            <span className="inline-flex items-center gap-1">
              <Gauge className="h-3 w-3" />
              {listing.brand}
              {listing.model ? ` ${listing.model}` : ""}
            </span>
          )}
          {listing.year && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {listing.year}
            </span>
          )}
          {listing.hours != null && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {Number(listing.hours).toLocaleString("fr-BE")} h
            </span>
          )}
        </div>

        {!compact && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-matpro-anthracite-muted">
            <MapPin className="h-3 w-3 text-matpro-green" />
            <span className="truncate">
              {listing.city}
              {listing.distance_km != null && (
                <>
                  {" "}
                  · <span className="font-semibold text-matpro-green">{listing.distance_km} km</span>
                </>
              )}
            </span>
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-matpro-border flex items-center justify-between text-[11px] text-matpro-anthracite-muted gap-2">
          {!compact ? (
            <div className="truncate flex flex-col gap-0.5 min-w-0">
              <div className="truncate flex items-center gap-1.5 min-w-0">
                {isPro ? (
                  <span className="inline-flex items-center gap-1 font-semibold text-matpro-green min-w-0">
                    <ShieldCheck className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{seller.company_name || t("card.professional")}</span>
                  </span>
                ) : (
                  <span>{t("card.individual")}</span>
                )}
              </div>
              {hasSellerRating && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-matpro-anthracite">
                  <StarRating value={sellerRating.average} size={4} />
                  <span>{sellerRating.average.toFixed(1)}</span>
                  <span className="text-matpro-anthracite-muted">({sellerRating.count})</span>
                </span>
              )}
            </div>
          ) : (
            <span className="truncate">{listing.city || ""}</span>
          )}
          {listing.created_at && (
            <span title={listing.created_at} className="flex-shrink-0">
              {formatDistanceToNow(new Date(listing.created_at), {
                addSuffix: true,
                locale: DATE_LOCALES[lang] || fr,
              })}
            </span>
          )}
        </div>
      </div>
  );

  const detailTarget = `/annonce/${listing.id}`;

  return (
    <div
      className={`group relative flex flex-col bg-white border rounded-md overflow-hidden shadow-sm transition-[transform,box-shadow,border-color] duration-200 ${
        selected
          ? "border-matpro-green ring-2 ring-matpro-green/30 -translate-y-0.5 shadow-lg"
          : isPremium
          ? "border-matpro-green ring-1 ring-matpro-green/20 hover:-translate-y-1 hover:shadow-lg"
          : isHighlighted
          ? "border-matpro-green hover:-translate-y-1 hover:shadow-lg"
          : "border-matpro-border hover:border-matpro-green hover:-translate-y-1 hover:shadow-lg"
      }`}
      data-testid={testId}
      data-selected={selected ? "true" : undefined}
    >
      {navigateOnClick ? (
        <Link to={detailTarget} className="block" onClick={() => onSelect?.(listing.id)}>
          {photoBlock}
        </Link>
      ) : (
        <button type="button" className="block w-full text-left" onClick={() => onSelect?.(listing.id)}>
          {photoBlock}
        </button>
      )}

      <ListingCardActions listing={listing} />

      {navigateOnClick ? (
        <Link to={detailTarget} className="flex flex-col flex-1" onClick={() => onSelect?.(listing.id)}>
          {bodyBlock}
        </Link>
      ) : (
        <button type="button" className="flex flex-col flex-1 text-left w-full" onClick={() => onSelect?.(listing.id)}>
          {bodyBlock}
        </button>
      )}

      {((hasShop && (seller.id || listing.seller_id) && !compact) || showCompare) ? (
        <div className="flex items-center gap-2 border-t border-matpro-border px-3 py-2 bg-matpro-bg/40">
          {hasShop && (seller.id || listing.seller_id) && !compact ? (
            <Link
              to={`/pro/${seller.id || listing.seller_id}`}
              onClick={stopBubble}
              className="inline-flex min-h-11 flex-1 items-center justify-center gap-1 rounded-md border border-matpro-green/30 bg-white px-2 py-2 text-[11px] font-bold text-matpro-green hover:bg-matpro-green-light"
              data-testid={`listing-matpass-shop-${listing.id}`}
            >
              <Store className="h-3.5 w-3.5" />
              {t("card.matpass_supplier")}
            </Link>
          ) : null}
          {showCompare ? (
            <button
              type="button"
              onClick={(e) => {
                stop(e);
                onToggleCompare?.(listing.id);
              }}
              data-testid={`listing-compare-toggle-${listing.id}`}
              aria-pressed={compareSelected}
              className={`inline-flex min-h-11 items-center justify-center rounded-md border px-3 py-2 text-[11px] font-bold transition-colors ${
                compareSelected
                  ? "border-matpro-green bg-matpro-green text-white"
                  : "border-matpro-border bg-white text-matpro-anthracite hover:border-matpro-green"
              }`}
            >
              {compareSelected ? t("compare.added") : t("compare.add")}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function formatPrice(price) {
  if (price == null) return "—";
  const hasDecimals = Math.abs(price - Math.round(price)) > 0.001;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(price);
}
